import React from "react";
import Avatar from "components/avatar";
import Typography from "components/typography";
import { useAppTranslation } from "locale/useAppTranslation";
import { InfoFieldProps, CustomerDetailCardProps } from "_interfaces/customer.interfaces";


const InfoField: React.FC<InfoFieldProps> = ({ label, value }) => (
    <div className="flex flex-col gap-1">
        <Typography variant="bodySmall" tone="secondary" className="font-medium text-greyScale-60">
            {label}
        </Typography>
        <Typography variant="body" className="font-semibold text-text-primary">
            {value || "—"}
        </Typography>
    </div>
);

const CustomerDetailCard: React.FC<CustomerDetailCardProps> = ({ detail }) => {
    const { t } = useAppTranslation("vendor");

    const isActive = detail.status?.toLowerCase() === "active";

    // Primary branch contact information fallback
    const primaryBranch = detail.branches?.[0];
    const vendorEmail = detail.email || primaryBranch?.pic_email;
    const contactPersonName = primaryBranch?.pic_name;
    const contactPersonPhone = primaryBranch?.pic_phone;
    const vendorLogo = detail.vendor_logo;

    return (
        <section className="rounded-2xl border border-border bg-white shadow-sm overflow-hidden">
            {/* Top Row: Avatar + Name + Subtitle + Status */}
            <div className="flex items-center justify-between p-6">
                <div className="flex items-center gap-4">
                    <Avatar size="xl" name={detail.customer_name} {...(detail.customer_photo_url ? { src: detail.customer_photo_url } : {})} />
                    <div className="flex flex-col gap-1">
                        <Typography variant="heading4" className="font-bold text-text-primary">
                            {detail.customer_name || "—"}
                        </Typography>
                        <div className="flex items-center gap-1.5 text-sm text-greyScale-60">
                            <span className="capitalize">{detail.customer_type || "Company"}</span>
                            <span>•</span>
                            <span>{detail.customer_code || "—"}</span>
                        </div>
                    </div>
                </div>

                <div
                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold capitalize ${isActive
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-greyScale-20 text-greyScale-60 border border-greyScale-30"
                        }`}
                >
                    {detail.status || "—"}
                </div>
            </div>

            {/* Dotted Divider Line */}
            <div className="px-6">
                <div className="border-t border-dashed border-border" />
            </div>

            {/* Main Info Grid */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 p-6">
                <InfoField
                    label={t("label-legal-name") || "Legal Name"}
                    value={detail.legal_name}
                />
                <InfoField
                    label={t("label-vendor-email") || "Customer Email"}
                    value={vendorEmail}
                />
                <InfoField
                    label={t("label-contact-person-name") || "Contact Person Name"}
                    value={contactPersonName}
                />
                <InfoField
                    label={t("label-contact-person-phone") || "Contact Person Phone Number"}
                    value={contactPersonPhone}
                />
            </div>
        </section>
    );
};

export default CustomerDetailCard;
